import axios from 'axios'

import { uploadStatus } from '@/common/app-file-upload/uploadStatus'
import { uploadTypes } from '@/common/app-file-upload/uploadTypes'
import { FileToUpload, FolderToUpload } from '@/models/documentToUpload.model'
import { AppUploadHandlerState } from '@/store/modules/app-upload-handler/state'
import { GET_PAGINATED_DOCUMENTS, LOAD_FOLDERS_TREE} from '@/store/modules/documents/action_types'
import { SET_NUMBERING_SNACKBAR_IS_OPEN } from '@/store/modules/documents/mutation_types'
import { GET_ROOM_STATS } from '@/store/modules/room/action_types'
import { SET_ROOM_STORAGE_EXCEEDED_DIALOG_IS_OPEN } from '@/store/modules/room/mutation_types'

import { AppUploadHandlerActionTypes } from './action_types'
import { AppUploadHandlerMutationTypes } from './mutation_types'
import documentsService from '../../../services/documents.service'
import { GET_TASK_FOLDER } from '../checklist/action_types'

export const actions: StoreActions<AppUploadHandlerActionTypes, AppUploadHandlerMutationTypes> = {
  async [AppUploadHandlerActionTypes.ADD_FILES_TO_UPLOAD] ({ commit, dispatch }, documents) {
    for (const document of documents) {
      if (document instanceof FileToUpload) {
        commit(AppUploadHandlerMutationTypes.ADD_FILE_TO_UPLOAD, document)
        commit(AppUploadHandlerMutationTypes.ADD_TO_QUEUE, document)
      } else if (document instanceof FolderToUpload) {
        // Log if it's a folder for sentry's logs
        console.info('Trying to upload a folder')
        const folder = await dispatch(AppUploadHandlerActionTypes.CREATE_FOLDER, document)
        commit(AppUploadHandlerMutationTypes.ADD_FILE_TO_UPLOAD, folder)
      }
    }
    dispatch(AppUploadHandlerActionTypes.PROCESS_QUEUE)
  },
  async [AppUploadHandlerActionTypes.CREATE_FOLDER] ({ commit, dispatch }, folder) {
    return await documentsService.createNewFolder(
      folder.roomMnemo,
      {
        name: folder.name,
        parentId: folder.parentId,
      })
      .then(async response => {
        for (const document of folder.children) {
          document.parentId = response.data.folder.id
          if (document.type === 'folder') {
            await dispatch(AppUploadHandlerActionTypes.CREATE_FOLDER, document)
          }
          if (document.type === 'file') {
            commit(AppUploadHandlerMutationTypes.ADD_TO_QUEUE, document)
          }
        }
        return folder
      })
  },
  async [AppUploadHandlerActionTypes.PROCESS_QUEUE] ({ state, commit, dispatch }) {
    const fileToUpload = (state as AppUploadHandlerState).uploadQueue.find(file => file.status === 'pending')
    if (fileToUpload && ((state as AppUploadHandlerState).uploadInProgress < (state as AppUploadHandlerState).simultaneousFileUpload)) {
      dispatch(AppUploadHandlerActionTypes.INCREMENT_UPLOAD_IN_PROGRESS)
      fileToUpload.status = uploadStatus.IN_PROGRESS
      dispatch(AppUploadHandlerActionTypes.PROCESS_QUEUE)
      try {
        await documentsService.postFile(fileToUpload)
        fileToUpload.status = uploadStatus.SUCCESS
      } catch (error: any) {
        /*
        * This error is triggered uniquely for pay per use data rooms
        */
        if (error?.response?.data?.errorCode === 'ERR_STORAGE_EXCEEDED') {
          dispatch(AppUploadHandlerActionTypes.CANCEL_ALL)
          commit(`room/${SET_ROOM_STORAGE_EXCEEDED_DIALOG_IS_OPEN}`, true, { root: true })
        }
        if (axios.isCancel(error)) {
          fileToUpload.status = uploadStatus.CANCELED
          fileToUpload.progress = 0
        } else {
          fileToUpload.status = uploadStatus.ERROR
          if (error?.response?.data?.errorCode) {
            fileToUpload.error = error.response.data.errorCode
          }
        }

        if (fileToUpload.status !== uploadStatus.CANCELED) {
          if (fileToUpload.fileObject.webkitRelativePath !== '') {
            console.info('File path : ', fileToUpload.fileObject.webkitRelativePath)
          } else {
            console.info('File name : ', fileToUpload.fileObject.name)
          }
        }
      } finally {
        dispatch(AppUploadHandlerActionTypes.DECREMENT_UPLOAD_IN_PROGRESS)
        dispatch(AppUploadHandlerActionTypes.PROCESS_QUEUE)
      }
    } else if ((state as AppUploadHandlerState).uploadQueue.every(file => file.status === uploadStatus.SUCCESS || file.status === uploadStatus.CANCELED)) {
      setTimeout(() => { dispatch(AppUploadHandlerActionTypes.UPLOAD_FINISHED) }, 3000)
    }
  },
  [AppUploadHandlerActionTypes.INCREMENT_UPLOAD_IN_PROGRESS] ({ commit }) {
    commit(AppUploadHandlerMutationTypes.INCREMENT_UPLOAD_IN_PROGRESS)
  },
  [AppUploadHandlerActionTypes.DECREMENT_UPLOAD_IN_PROGRESS] ({ commit }) {
    commit(AppUploadHandlerMutationTypes.DECREMENT_UPLOAD_IN_PROGRESS)
  },
  [AppUploadHandlerActionTypes.REDO_FILE_UPLOAD]: ({ commit, dispatch }, fileKey) => {
    commit(AppUploadHandlerMutationTypes.SET_FILE_STATUS_TO_PENDING, fileKey)
    dispatch(AppUploadHandlerActionTypes.PROCESS_QUEUE)
  },
  [AppUploadHandlerActionTypes.CANCEL_ALL] ({ state, commit }) {
    (state as AppUploadHandlerState).uploadQueue.forEach(file => {
      if (file.status === uploadStatus.IN_PROGRESS) {
        file.cancelToken?.cancel()
      } else if (file.status === uploadStatus.PENDING) { file.status = uploadStatus.CANCELED }
    })
    commit(AppUploadHandlerMutationTypes.RESET)
  },
  [AppUploadHandlerActionTypes.CANCEL_FILE] ({ state }, fileKey)  {
    const file = (state as AppUploadHandlerState).uploadQueue.find(file => file.key === fileKey)

    if (!file) {
      return
    }

    if (file.status === uploadStatus.IN_PROGRESS) {
      file.cancelToken?.cancel()
      file.status = uploadStatus.CANCELED
    } else if (file.status === uploadStatus.PENDING) {
      file.status = uploadStatus.CANCELED
    }
  },
  [AppUploadHandlerActionTypes.UPLOAD_FINISHED] ({ commit, dispatch, state, rootState, rootGetters }) {
    if ((state as AppUploadHandlerState).uploadQueue.some(item => item.status === uploadStatus.SUCCESS && item.uploadType === uploadTypes.DOCUMENT)) {
      dispatch(`documents/${GET_PAGINATED_DOCUMENTS}`, undefined, { root: true })
      dispatch(`documents/${LOAD_FOLDERS_TREE}`, undefined, { root: true })

      if ((rootState['documents'] as any).numberingEnabled) {
        commit(`documents/${SET_NUMBERING_SNACKBAR_IS_OPEN}`, true, { root: true })
      }
    }

    const checklistUploaded = (state as AppUploadHandlerState).uploadQueue.find(item => item.status === uploadStatus.SUCCESS && item.uploadType === uploadTypes.CHECKLIST)

    if (checklistUploaded) {
      dispatch(`checklist/${GET_TASK_FOLDER}`, {
        mnemo: checklistUploaded.roomMnemo,
        folderId: checklistUploaded.parentId,
        queryObject: {
          markRecent: true,
        },
      }, { root: true })
    }
    if ((state as AppUploadHandlerState).uploadQueue.some(item => item.uploadType === uploadTypes.DOCUMENT && rootGetters['room/isDataRoom'])) {
      dispatch(`room/${GET_ROOM_STATS}`, rootGetters['room/roomMnemo'], { root: true })
    }

    commit(AppUploadHandlerMutationTypes.RESET)
  },
}
